/**
 * A Discover card's cover: the model's canvas snapshot when there is no
 * avatar, else a branded gradient backdrop with the avatar contained, or a
 * monogram while the snapshot is pending or unavailable.
 */
import {useState} from 'react';
import ApiImage from '@/components/common/ApiImage';
import {useModelPreview} from './useModelPreview';
import {HERO_PALETTES, hashStr} from './discoverArt';

/** Cover for shelf / result cards: the model's canvas snapshot when there is
 *  no avatar, else a branded gradient backdrop with the avatar contained, or a
 *  monogram while the snapshot is pending/unavailable. */
export function CoverArt({id, label, height = 130, src, version}: Readonly<{
    id: string;
    label: string;
    height?: number;
    src?: string;
    version?: string | number
}>) {
    const [imgErr, setImgErr] = useState(false);
    const seed = hashStr(id + label);
    const [c1, c2, c3] = HERO_PALETTES[seed % HERO_PALETTES.length];
    const angle = seed % 360;
    const showImg = !!src && !imgErr;
    const monogram = label.charAt(0).toUpperCase();
    // Only attempt a canvas snapshot when there is no usable avatar.
    const {ref, dataUrl} = useModelPreview(id, version, !showImg);

    const renderArt = () => {
        if (showImg) {
            return (
                <>
                    {/* Darkened plate so light / transparent logos read clearly */}
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'radial-gradient(120% 120% at 50% 40%, rgba(8,12,24,0.5), rgba(6,10,20,0.86))'
                    }}/>
                    <ApiImage
                        src={src}
                        alt=""
                        loading="lazy"
                        onError={() => setImgErr(true)}
                        style={{
                            position: 'relative',
                            maxWidth: '82%',
                            maxHeight: '74%',
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.45))'
                        }}
                    />
                </>
            );
        }
        if (dataUrl) {
            return (
                <>
                    {/* Light touch of darkening lets the palette stay vivid; the model's
                    own contrast + drop-shadow-sm (below) are what make it pop. */}
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'radial-gradient(120% 120% at 50% 40%, rgba(8,12,24,0.26), rgba(6,10,20,0.6))'
                    }}/>
                    <img
                        src={dataUrl}
                        alt=""
                        style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            padding: 6,
                            filter: 'contrast(1.12) brightness(1.1) drop-shadow(0 2px 6px rgba(0,0,0,0.55))',
                            animation: 'edit-fade 0.4s ease both'
                        }}
                    />
                </>
            );
        }
        return (
            <span style={{
                position: 'relative',
                color: 'rgba(255,255,255,0.92)',
                fontSize: 34,
                fontWeight: 800,
                lineHeight: 1,
                letterSpacing: '-0.04em',
                textShadow: '0 4px 18px rgba(0,0,0,0.35)'
            }}>
                {monogram}
            </span>
        );
    };

    return (
        <div ref={ref} style={{
            position: 'relative',
            height,
            overflow: 'hidden',
            background: `linear-gradient(${angle}deg, ${c1}, ${c2}, ${c3})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <div style={{
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(70% 80% at 30% 40%, ${c3}aa, transparent)`,
                mixBlendMode: 'screen'
            }}/>
            {renderArt()}
            <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, transparent 55%, rgba(8,14,28,0.5))'
            }}/>
        </div>
    );
}
